// src/components/map/MapboxWrapper.tsx
//
// Thin shell after concern-based decomposition (Phases 1–8).
//
// This component is responsible for:
//   1. Declaring shared refs and ref-sync effects
//   2. Composing the decomposed adapter hooks in dependency order
//   3. Calling external hook compositions (shapefile, geombin, layer sorting, etc.)
//   4. A handful of effects that don't belong to any single concern
//   5. Rendering the map container DOM
//
// All business logic has been extracted into:
//   - useMapFacilityHighlighting  (Phase 6)
//   - useMapWellHighlighting      (Phase 6)
//   - useMapTileSourceManager     (Phase 7)
//   - useMapLifecycle             (Phase 8)
//   - useMapAttentionOverlay      (Phase 2)
//   - useMapNavigation            (Phase 3)
//   - useMapClickSelection        (Phase 4)
//   - useMapHoverLegend           (Phase 5)
import { MutableRefObject, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";

import MapboxDraw from "@mapbox/mapbox-gl-draw";
import { feature, featureCollection } from "@turf/helpers";
import {
  BASE_WELL_DEFAULT_ZOOM,
  BASE_WELL_LAYER,
  BASE_WELL_RASTER_LAYER,
  HZ_RASTER_MVT_CROSSOVER_ZOOM,
  HZ_WELL_RASTER_LAYER,
  SELECTED_SINGLE_WELL_LAYER,
  SELECTED_SINGLE_WELL_POINT_GLOW_LAYER,
  SELECTED_SINGLE_WELL_POINT_LAYER,
  WELL_LAYER,
  WELL_LAYER_POINT,
  WELL_SOURCE_IDS,
  highlightDotRadius,
  highlightGlowRadius
} from "constants/mapLayers.constants";
import mapboxgl from "mapbox-gl";
import { PadScenario } from "store/features";
import { RootState } from "store/rootReducer";
import type { ISelectedWell } from "stores/map";
import {
  selectBaseLayerFilterId,
  selectBaseLayerResourcePlays,
  selectDisplaySettings,
  selectSelectedWells,
  selectTxnId,
  selectViewLock,
  useMapStore
} from "stores/map";
import { selectWellCardOutlineUwi, useWellCardPanelStore } from "stores/wellCardPanel";

import useBetaFeatures from "hooks/useBetaFeatures";

import { useDashboardContext } from "components/dashboard/hooks/useDashboardContext";
import useManageMapboxShapefileLoading from "components/map/hooks/manage-mapbox-shapefile-loading/useManageMapboxShapefileLoading";
import { useHeaderDataSources } from "components/org-settings/hooks/useHeaderDataSources";
import { useSurveyDataSources } from "components/org-settings/hooks/useSurveyDataSources";
import { useProjectContext } from "components/project/projects/context";

import useUserDefaults from "../../components/user/hooks/useUserDefaults";
import useUserSettings from "../../components/user/hooks/useUserSettings";
import { EntityKind } from "../../models/entityKind";
import { useManageMapboxLayerSorting, useManageMapboxLayerStyles } from "./hooks";
import { useMapAttentionOverlay } from "./hooks/adapters/useMapAttentionOverlay";
import { useMapClickSelection } from "./hooks/adapters/useMapClickSelection";
import { useMapFacilityHighlighting } from "./hooks/adapters/useMapFacilityHighlighting";
import { useMapHoverLegend } from "./hooks/adapters/useMapHoverLegend";
import { useMapLifecycle } from "./hooks/adapters/useMapLifecycle";
import { useMapNavigation } from "./hooks/adapters/useMapNavigation";
import { useMapTileSourceManager } from "./hooks/adapters/useMapTileSourceManager";
import { useMapWellHighlighting } from "./hooks/adapters/useMapWellHighlighting";
import { useRigFracColorBy } from "./hooks/adapters/useRigFracColorBy";
import { useRigFracIconSize } from "./hooks/adapters/useRigFracIconSize";
import { useRigFracLayerFilter } from "./hooks/adapters/useRigFracLayerFilter";
import useManageMapboxGeomBinLoading from "./hooks/manage-mapbox-geombin-loading/useManageMapboxGeomBinLoading";
import { useBaseLayerRefactor } from "./hooks/useBaseLayerRefactor";
import useDataAccess from "./hooks/useDataAccess";
import useDataSouceAvailability from "./hooks/useDataSourceAvailability";
import { useHighlightSelection } from "./hooks/useHighlightSelection";
import { useLabelDensity } from "./hooks/useLabelDensity";
import { useMapContext } from "./hooks/useMapContext";
import { useMapDispatch } from "./hooks/useMapDispatch";
import { useMapRulerControl } from "./hooks/useMapRulerControl";
import { useMapScaleControl } from "./hooks/useMapScaleControl";
import { useSyncWellHighlighting } from "./hooks/useSyncWellHighlighting";
import {
  BASE_WELL_LINES_LAYER,
  BASE_WELL_PAINT,
  applyBaseWellStyle,
  resolveBaseWellStyle
} from "./utils/baseWellStyle";
import {
  BASE_WELL_MAX_RETRIES,
  BASE_WELL_RASTER_SOURCE_ID,
  BASE_WELL_RETRY_BASE_MS,
  BASE_WELL_SOURCE_ID,
  onBaseWellTileNotReady
} from "./utils/baseWellTileErrors";
import { emptyFeatureCollection } from "./utils/emptyFeatureCollection";
import {
  getBaseWellLayerTileUrl,
  getBaseWellRasterTileUrl,
  getLegacyBaseWellLayerTileUrl
} from "./utils/getBaseWellLayerTileUrl";
import { keepMissingFeatures } from "./utils/keepMissingFeatures";
import { isQuantileBinningActive } from "./utils/quantileBinning";
import {
  buildHeaderPreferenceToken,
  buildSurveyPreferenceToken
} from "./utils/surveyPreferenceToken";

// ── Types ────────────────────────────────────────────────────────────────────

export interface MapboxWrapperComponentModel {
  addLayer;
  drawRef: MutableRefObject<MapboxDraw>;
  id: string;
  mapboxRef: MutableRefObject<mapboxgl.Map>;
  removeLayer;
  setLayers;
}

// ── Component ────────────────────────────────────────────────────────────────

export default function MapboxWrapper({
  addLayer,
  drawRef,
  id,
  mapboxRef,
  removeLayer
}: MapboxWrapperComponentModel): JSX.Element {
  // ── Store / Redux selectors ────────────────────────────────────────────
  const setSelectedWells = useMapStore((s) => s.setSelectedWells);
  const isMountedRef = useRef(true);
  const activeEntityKinds = useSelector(
    (state: RootState) => state.app.activeEntityKinds
  );
  const orgId = useSelector((state: RootState) => state.auth.user.organization?.id);
  const token = useSelector((state: RootState) => state.auth.jwtToken);
  const checkedPadScenarios: PadScenario[] = useSelector(
    (state: RootState) => state.app.checkedPadScenarios
  );
  const isFiltering = useSelector((state: RootState) => state.filter.isFiltering);
  const selectedGroups = useSelector((state: RootState) => state.groupBy.selectedGroups);
  const displaySettings = useMapStore(selectDisplaySettings);
  const isViewLocked = useMapStore(selectViewLock);
  const selectedWells: Record<string, ISelectedWell> = useMapStore(selectSelectedWells);
  // The well the preview popup is on, else the card's well while it holds the
  // outline. The rules are with `outlineUwi` below.
  const cardUwi = useWellCardPanelStore(selectWellCardOutlineUwi);
  const txnId = useMapStore(selectTxnId);
  const syncWells = useSelector((state: RootState) => state.app.syncWells);
  // The base well layer's own filterId (EVA-35) — see useBaseLayerFilterSync,
  // which posts it independent of the well-list filter. No filterId yet →
  // the tile URL builder returns undefined → the effect below must not
  // create the source/layers.
  const baseLayerFilterId = useMapStore(selectBaseLayerFilterId);
  // The base layer's tiles are built from the org's survey preference, but
  // the server resolves that from the JWT, so nothing in the URL changed
  // when the preference did and Mapbox kept serving cached tiles. This token
  // puts it back in the URL — useSurveyDataSources reads the same
  // react-query key the preference screen invalidates on save, so a change
  // lands without a reload.
  const surveyDataSources = useSurveyDataSources();
  const surveyPreferenceToken = useMemo(
    () => buildSurveyPreferenceToken(surveyDataSources),
    [surveyDataSources]
  );
  // Same idea, for the org's HEADER-source preference — a separate,
  // independently-settable order that decides which source's
  // Header.ResourcePlay wins (see buildHeaderPreferenceToken). Both the MVT
  // and raster tile URLs below carry this (EVA-35 PR #6601 round-4 review):
  // the raster's PIXELS don't depend on it, but the WELL SET it's rendered
  // from does — see getBaseWellRasterTileUrl.
  const headerDataSources = useHeaderDataSources();
  const headerPreferenceToken = useMemo(
    () => buildHeaderPreferenceToken(headerDataSources),
    [headerDataSources]
  );
  const rawBaseWellStyle = displaySettings.layerStyleSettings?.[BASE_WELL_LAYER];
  const baseWellStyle = useMemo(
    () => resolveBaseWellStyle(rawBaseWellStyle),
    [rawBaseWellStyle]
  );
  const isDefaultMapSettingsLoaded = useSelector(
    (state: RootState) => state.userSetting.isDefaultSettingsLoaded
  );

  // ── Context ────────────────────────────────────────────────────────────
  const { capabilities, lockMap } = useDashboardContext();
  // Persisted layer-tree checkbox state (survives reload via session/view
  // restore) — the base-well effect below reads this directly so the layers
  // it adds/updates on mapbox always start in sync with the checkbox,
  // regardless of whether this effect or useMapLifecycle's style.load pass
  // wins the race to create the layers first.
  const { checkedLayerKeys } = useProjectContext();
  const mapDispatch = useMapDispatch();
  const {
    entitiesInGroup,
    facilitiesInGroup,
    highlightedFeatures,
    isMapInitialized,
    isPolygonFilterControlVisible,
    isScreenshotMode,
    isStyleLoaded
  } = useMapContext();

  const activeEntities = useSelector((state: RootState) => state.app.activeEntityKinds);
  const wellGroupBy = useSelector((state: RootState) => state.groupBy.globalGroupBy);
  const facilityGroupBy = useSelector(
    (state: RootState) => state.groupBy.globalFacilityFocus
  );

  // Composing a screenshot suspends the viewport-driven filter only when the
  // legend's numbers would move underneath it — quantile boundaries are computed
  // from the well population. Every other bin type keeps refiltering as the map
  // moves, so a user composing a frame can still pan to bring wells in and out.
  // That includes the canvas resize on entry: the overlay widens the viewport,
  // `resize()` fires `moveend`, and the widened viewport becomes the map's
  // polygon. Intended — the expanded map is meant to show what it now covers.
  const isExtentSyncFrozen =
    isScreenshotMode &&
    isQuantileBinningActive({
      activeEntityKinds: activeEntities,
      wellGroupBy,
      facilityGroupBy
    });

  // ── Data access hooks ──────────────────────────────────────────────────
  const { hasFeature } = useBetaFeatures();
  const { autoZoomSetting, zoomToFilterSetting } = useUserSettings();
  const defaults = useUserDefaults();
  const defaultBaseLayer = defaults?.mapStyle?.style;
  const baseLayerRefactor = useBaseLayerRefactor();
  // Legacy-only inputs (EVA-35 flag off) — the pre-refactor base layer's gate
  // and source selection.
  const { hasSurvey } = useDataAccess();
  const { hasGdc, hasIhs } = useDataSouceAvailability();
  const baseLayerResourcePlays = useMapStore(selectBaseLayerResourcePlays);

  // ── Shared refs (written by ref-sync effects, read by hooks) ───────────
  const entitiesInGroupRef = useRef(entitiesInGroup);
  const facilitiesInGroupRef = useRef(facilitiesInGroup);
  const isPolygonFilterControlVisibleRef = useRef(isPolygonFilterControlVisible);
  const selectedGroupsRef = useRef(selectedGroups);
  const tokenRef = useRef<string>(token);
  const txnIdRef = useRef(txnId);
  const checkedPadScenariosRef = useRef(checkedPadScenarios);
  const viewLockedByDashboardRef = useRef(lockMap);
  const viewLockedRef = useRef(isViewLocked);
  const extentSyncFrozenRef = useRef(isExtentSyncFrozen);
  const activeEntitiesRef = useRef(activeEntities);

  // ── Ref-sync effects ───────────────────────────────────────────────────
  useEffect(() => {
    entitiesInGroupRef.current = entitiesInGroup;
  }, [entitiesInGroup]);
  useEffect(() => {
    facilitiesInGroupRef.current = facilitiesInGroup;
  }, [facilitiesInGroup]);
  useEffect(() => {
    isPolygonFilterControlVisibleRef.current = isPolygonFilterControlVisible;
  }, [isPolygonFilterControlVisible]);
  useEffect(() => {
    selectedGroupsRef.current = selectedGroups;
  }, [selectedGroups]);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);
  useEffect(() => {
    txnIdRef.current = txnId;
  }, [txnId]);
  useEffect(() => {
    checkedPadScenariosRef.current = checkedPadScenarios;
  }, [checkedPadScenarios]);
  useEffect(() => {
    viewLockedByDashboardRef.current = lockMap;
  }, [lockMap]);
  useEffect(() => {
    viewLockedRef.current = isViewLocked;
  }, [isViewLocked]);
  useEffect(() => {
    extentSyncFrozenRef.current = isExtentSyncFrozen;
  }, [isExtentSyncFrozen]);
  useEffect(() => {
    activeEntitiesRef.current = activeEntities;
  }, [activeEntities]);

  // ── Active entities well-clearing effect ───────────────────────────────
  useEffect(() => {
    const hasWellsOnMap = Object.keys(entitiesInGroup).length > 0;
    if (hasWellsOnMap && !activeEntities.includes(EntityKind.Well)) {
      setSelectedWells({});
    }
  }, [activeEntities]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── isFiltering → loading dispatch ─────────────────────────────────────
  useEffect(() => {
    if (isMountedRef.current) {
      mapDispatch({
        payload: {
          isLoading: isFiltering
        }
      });
    }
  }, [isFiltering, mapDispatch]);

  /*
   * Outline the one well the map is describing: the well the preview popup is
   * on, else the well the card is open on, else a single selection on a
   * dashboard that shows one well. The preview wins because it is the well the
   * user is looking at now.
   *
   * The card keeps the outline on its well until the user moves the preview to
   * another well or presses Escape, and picking a well for the card brings it
   * back. A well the user has moved on from stays dark when the selection
   * clears.
   *
   * No capability gate on the preview's or the card's path: the card IS the
   * widget asking for the outline, and it opens on dashboards that have none
   * of the three below.
   * The sync-highlight setting still applies - that turns map highlighting off
   * altogether.
   */
  const isSyncHighlightOff =
    syncWells?.isSync === true && syncWells?.highlightSyncedWell === false;
  const hasSingleWellWidget =
    capabilities.hasWellCardWidget ||
    capabilities.hasSingleWellWidget ||
    capabilities.hasMultiProductWidget;
  const singleSelectedUwi =
    Object.keys(selectedWells).length === 1 && hasSingleWellWidget
      ? Object.keys(selectedWells)[0]
      : null;
  const outlineUwi = isSyncHighlightOff ? null : cardUwi ?? singleSelectedUwi;

  // What each source was last given. setData repaints even on identical data,
  // and that repaint fires `idle` again, so the tile listeners below would
  // otherwise feed themselves forever.
  const lastOutlineKeyRef = useRef<Nullable<string>>(null);
  const lastOutlineDotKeyRef = useRef<Nullable<string>>(null);
  const lastOutlineLinesRef = useRef<GeoJSON.Feature[]>([]);
  const lastOutlineDotsRef = useRef<GeoJSON.Feature[]>([]);

  const paintSingleWellOutline = () => {
    if (!mapboxRef.current) {
      return;
    }
    const mapbox = mapboxRef.current;
    const source = mapbox.getSource(SELECTED_SINGLE_WELL_LAYER) as mapboxgl.GeoJSONSource;
    const pointSource = mapbox.getSource(
      SELECTED_SINGLE_WELL_POINT_LAYER
    ) as mapboxgl.GeoJSONSource;

    if (!source) {
      return;
    }

    const setIfChanged = (
      target: Maybe<mapboxgl.GeoJSONSource>,
      data: GeoJSON.FeatureCollection,
      key: string,
      lastKeyRef: MutableRefObject<Nullable<string>>
    ) => {
      if (!target || key === lastKeyRef.current) return;
      target.setData(data);
      lastKeyRef.current = key;
    };

    if (!outlineUwi) {
      setIfChanged(source, { ...emptyFeatureCollection }, "", lastOutlineKeyRef);
      setIfChanged(pointSource, { ...emptyFeatureCollection }, "", lastOutlineDotKeyRef);
      lastOutlineLinesRef.current = [];
      lastOutlineDotsRef.current = [];
      return;
    }

    const uwi = outlineUwi;
    const outlined = new Set([uwi]);
    const wells = mapbox.querySourceFeatures(WELL_LAYER, {
      sourceLayer: "eva-wells",
      filter: ["==", ["get", "Uwi"], uwi]
    });
    const lines = keepMissingFeatures(
      wells.map((feat) => feature(feat.geometry, { Uwi: uwi })),
      outlined,
      lastOutlineLinesRef.current
    );
    lastOutlineLinesRef.current = lines;

    // Keyed on coordinates because tiles swap in other clipped pieces of the
    // same line, often with the same number of vertices.
    setIfChanged(
      source,
      featureCollection(lines),
      `${uwi}:${JSON.stringify(
        lines.map((line) => (line.geometry as GeoJSON.LineString).coordinates)
      )}`,
      lastOutlineKeyRef
    );

    if (!pointSource) {
      return;
    }
    if (mapbox.getLayer(SELECTED_SINGLE_WELL_POINT_LAYER)) {
      mapbox.setPaintProperty(
        SELECTED_SINGLE_WELL_POINT_LAYER,
        "circle-radius",
        highlightDotRadius(displaySettings.wellDotThickness)
      );
    }
    if (mapbox.getLayer(SELECTED_SINGLE_WELL_POINT_GLOW_LAYER)) {
      mapbox.setPaintProperty(
        SELECTED_SINGLE_WELL_POINT_GLOW_LAYER,
        "circle-radius",
        highlightGlowRadius(displaySettings.wellDotThickness)
      );
    }
    // Guarded because querySourceFeatures throws on a source the map has not
    // added yet.
    const vertWells = mapbox.getSource(WELL_LAYER_POINT)
      ? mapbox.querySourceFeatures(WELL_LAYER_POINT, {
          sourceLayer: "eva-wells",
          filter: ["==", ["get", "Uwi"], uwi]
        })
      : [];
    // A multilateral's legs ride in the vertical tiles too, as lines.
    const dots = keepMissingFeatures(
      vertWells
        .filter((feat) => feat.geometry.type === "Point")
        .map((feat) => feature(feat.geometry, { Uwi: uwi })),
      outlined,
      lastOutlineDotsRef.current
    );
    lastOutlineDotsRef.current = dots;
    setIfChanged(
      pointSource,
      featureCollection(dots),
      dots
        .map((dot) => {
          const [lng, lat] = (dot.geometry as GeoJSON.Point).coordinates;
          return `${dot.properties?.Uwi}@${lng.toFixed(6)},${lat.toFixed(6)}`;
        })
        .join(","),
      lastOutlineDotKeyRef
    );
  };
  const paintSingleWellOutlineRef = useRef(paintSingleWellOutline);
  paintSingleWellOutlineRef.current = paintSingleWellOutline;

  // useSyncWellHighlighting empties both sources on its own when the sync
  // highlight flips, so the memos cannot be trusted across that change.
  useEffect(() => {
    lastOutlineKeyRef.current = null;
    lastOutlineDotKeyRef.current = null;
  }, [syncWells]);

  // ── txnId → single-well selection sync effect ──────────────────────────
  useEffect(() => {
    // Clear highlighted wells
    if (isMountedRef.current) {
      mapDispatch({
        payload: {
          highlightedFeatures: { line: [], point: [], facility: [] }
        }
      });
    }

    paintSingleWellOutline();
  }, [txnId, selectedWells, mapDispatch, capabilities, syncWells]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Well card → single-well outline effect ─────────────────────────────
  // Only the outline. The clear above stays with the selection, because a card
  // opening, closing or retargeting says nothing about the highlighted
  // features a cross-section derive has just written.
  useEffect(() => {
    paintSingleWellOutline();
  }, [cardUwi, displaySettings.wellDotThickness]); // eslint-disable-line react-hooks/exhaustive-deps

  // The card can open before the camera reaches its well, and a basemap switch
  // recreates both sources empty, so the outline repaints as well tiles load.
  useEffect(() => {
    const mapbox = mapboxRef.current;
    if (!mapbox || !isMapInitialized || !outlineUwi) return undefined;

    const repaint = () => paintSingleWellOutlineRef.current();
    const onWellTiles = (event?: { sourceId?: string; isSourceLoaded?: boolean }) => {
      const isWellSource = WELL_SOURCE_IDS.some(
        (sourceId) => sourceId === event?.sourceId
      );
      if (isWellSource && event?.isSourceLoaded) repaint();
    };
    // The recreated sources are empty, whatever the memos say they hold.
    const invalidate = () => {
      lastOutlineKeyRef.current = null;
      lastOutlineDotKeyRef.current = null;
    };

    mapbox.on("idle", repaint);
    mapbox.on("sourcedata", onWellTiles);
    mapbox.on("style.load", invalidate);
    return () => {
      mapbox.off("idle", repaint);
      mapbox.off("sourcedata", onWellTiles);
      mapbox.off("style.load", invalidate);
    };
  }, [outlineUwi, isMapInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── External hook composition (unchanged) ──────────────────────────────
  useManageMapboxShapefileLoading({ addLayer, removeLayer });
  useManageMapboxGeomBinLoading({ addLayer, removeLayer });
  useRigFracColorBy({ enabled: hasFeature("Rig And Frac Layers") });
  useRigFracIconSize({ enabled: hasFeature("Rig And Frac Layers") });
  useManageMapboxLayerSorting({ mapbox: mapboxRef?.current });
  useManageMapboxLayerStyles({ mapbox: mapboxRef?.current });
  useMapScaleControl(mapboxRef?.current);
  useMapRulerControl(mapboxRef?.current);
  useSyncWellHighlighting(mapboxRef?.current, SELECTED_SINGLE_WELL_LAYER);
  useSyncWellHighlighting(mapboxRef?.current, SELECTED_SINGLE_WELL_POINT_LAYER);

  // Persisted layer-tree checkbox state for the "Base Well" entry. Read as a
  // plain boolean (not the raw array) so it's a stable primitive dependency
  // for the effect below — it changes only when the checkbox is actually
  // toggled or restored from session/view, not on unrelated re-renders.
  const isBaseWellChecked = checkedLayerKeys.includes(BASE_WELL_LAYER);

  // ── Base well layer effect (resource plays filter) ─────────────────────
  useEffect(() => {
    if (!baseLayerRefactor) return;
    const mapbox = mapboxRef.current;
    if (!mapbox) return;

    // Only the legacy path ever creates a source named BASE_WELL_LAYER; if one
    // exists, the flag flipped mid-session and this effect owns tearing it down.
    if (mapbox.getSource(BASE_WELL_LAYER)) {
      if (mapbox.getLayer(BASE_WELL_LAYER)) {
        mapbox.removeLayer(BASE_WELL_LAYER);
      }
      mapbox.removeSource(BASE_WELL_LAYER);
    }

    const baseWellVisibility: "visible" | "none" = isBaseWellChecked ? "visible" : "none";

    const newTileUrl = getBaseWellLayerTileUrl({
      filterId: baseLayerFilterId,
      prefHash: surveyPreferenceToken,
      attrPrefHash: headerPreferenceToken
    });
    // Raster counterpart of newTileUrl (EVA-35 raster crossover) — same
    // filterId/prefHash/attrPrefHash contract, so it resolves exactly when
    // newTileUrl does and can't serve one header preference's well set under
    // another's URL (EVA-35 PR #6601 round-4 review) — see
    // getBaseWellRasterTileUrl.
    const newRasterTileUrl = getBaseWellRasterTileUrl({
      filterId: baseLayerFilterId,
      prefHash: surveyPreferenceToken,
      attrPrefHash: headerPreferenceToken,
      color: baseWellStyle.color
    });

    // No filterId yet — the base layer's first filter post (see
    // useBaseLayerFilterSync) hasn't resolved. `f` is required by the tile
    // endpoint (400 without it), so the source/layers must not be created
    // yet. This effect re-runs once baseLayerFilterId is set.
    if (!newTileUrl || !newRasterTileUrl) return;

    // getMapLayers (via useMapLifecycle's style.load pass) owns the initial
    // source + layers under the `${BASE_WELL_LAYER}-lines` source id. When they
    // already exist, a plays change only needs the tile URL swapped in place —
    // setTiles clears the source's tiles and refetches with the new query, with
    // no layer surgery and no isStyleLoaded() dependency. That gate matters:
    // isStyleLoaded() is false whenever ANY source is still loading, which on
    // this map is most of the time, so an effect gated on it almost never runs
    // and play changes silently never reached the server.
    const baseWellSourceId = BASE_WELL_LINES_LAYER;
    // The survey-lines layer shares this same id (source ids and layer ids
    // are separate namespaces in mapbox-gl), so one constant names both.
    const baseWellLinesLayerId = baseWellSourceId;

    // Default matches the legacy GeoServer layer (11-22). ZoomSlider writes the
    // override under the parent BASE_WELL_LAYER key; it applies to all three ids.
    const zoomSettings =
      displaySettings.layerZoomSettings?.[BASE_WELL_LAYER] || BASE_WELL_DEFAULT_ZOOM;
    // Below HZ_RASTER_MVT_CROSSOVER_ZOOM the raster tile takes over, exactly
    // like the interactive well layer's raster/MVT split — reusing the same
    // constant keeps the two crossovers from ever drifting apart. The user's
    // own zoom-slider range still bounds both ends of the combined range.
    // Capped at zoomSettings.maxzoom too: a user range entirely below the
    // crossover (e.g. 3-6) would otherwise raise the MVT minzoom to 8 while
    // maxzoom passes through unclamped at 6, giving mapbox an inverted range
    // (minzoom > maxzoom). Capping collapses that case to a zero-width range
    // (e.g. 6-6), which mapbox never renders — the MVT layers are cleanly
    // hidden instead of the raster half staying correct alongside a broken
    // MVT range. Ranges that span or sit above the crossover are unaffected,
    // since the crossover-clamped value never exceeds maxzoom there.
    const baseWellMvtMinzoom = Math.min(
      Math.max(zoomSettings.minzoom, HZ_RASTER_MVT_CROSSOVER_ZOOM),
      zoomSettings.maxzoom
    );
    // Floored at minzoom for the mirror-image case (a range entirely above the
    // crossover, e.g. the 11-22 default): collapses to zero width instead of
    // an inverted 11-8 range.
    const baseWellRasterMaxzoom = Math.max(
      Math.min(zoomSettings.maxzoom, HZ_RASTER_MVT_CROSSOVER_ZOOM),
      zoomSettings.minzoom
    );

    // Inserted just below the interactive raster layer when it's present (so
    // the base raster never paints over it), otherwise just below the
    // interactive well layer — same fallback the MVT `beforeId` below uses.
    // Computed as a function (not a single snapshot) because it's read again
    // later, after the interactive raster/MVT layers may have been added.
    const baseRasterBeforeId = () =>
      mapbox.getLayer(HZ_WELL_RASTER_LAYER)
        ? HZ_WELL_RASTER_LAYER
        : mapbox.getLayer(WELL_LAYER)
        ? WELL_LAYER
        : undefined;

    const addBaseWellRasterLayer = () => {
      // Guarded the same way the reconcile branch below guards its own
      // `getSource` check: the "genuinely missing" branch further down
      // removes the MVT lines/circle/raster LAYERS but never removes the
      // raster SOURCE, so if the raster source survived while the MVT source
      // did not, calling addSource unconditionally here throws ("there is
      // already a source with this ID") — after the MVT layers were already
      // added, so it would escape this useEffect body uncaught.
      if (!mapbox.getSource(BASE_WELL_RASTER_LAYER)) {
        mapbox.addSource(BASE_WELL_RASTER_LAYER, {
          type: "raster",
          tiles: [newRasterTileUrl],
          tileSize: 256
        });
      }
      mapbox.addLayer(
        {
          id: BASE_WELL_RASTER_LAYER,
          type: "raster",
          source: BASE_WELL_RASTER_LAYER,
          minzoom: zoomSettings.minzoom,
          maxzoom: baseWellRasterMaxzoom,
          layout: {
            visibility: baseWellVisibility
          },
          paint: BASE_WELL_PAINT[BASE_WELL_RASTER_LAYER](baseWellStyle)
        },
        baseRasterBeforeId()
      );
    };

    const existingSource = mapbox.getSource(baseWellSourceId) as
      | (mapboxgl.VectorSourceImpl & {
          setTiles?: (tiles: string[]) => void;
          tiles?: string[];
        })
      | undefined;
    if (existingSource) {
      if (
        typeof existingSource.setTiles === "function" &&
        existingSource.tiles?.[0] !== newTileUrl
      ) {
        existingSource.setTiles([newTileUrl]);
      }

      // The layers may have been (re)created by either this effect or
      // useMapLifecycle's style.load pass, and neither path is guaranteed to
      // apply the persisted checkbox state or the user's zoom range on
      // creation (getMapLayers has no displaySettings dependency, so it always
      // declares the 11-22 default). Re-assert both here so the map never
      // drifts out of sync with the "Base Well" checkbox or its zoom slider.
      for (const id of [baseWellLinesLayerId, BASE_WELL_LAYER]) {
        if (mapbox.getLayer(id)) {
          mapbox.setLayoutProperty(id, "visibility", baseWellVisibility);
          mapbox.setLayerZoomRange(id, baseWellMvtMinzoom, zoomSettings.maxzoom);
        }
      }

      // Raster half of the crossover. Normally (re)created in lockstep with
      // the MVT source above — both this effect and getMapLayers's style.load
      // pass create them together — but it's handled independently here for
      // the same reason updateWellAndFacilityTileUri treats the interactive
      // well layer's MVT and raster sources independently: defensive parity,
      // not an expected steady-state gap.
      const existingRasterSource = mapbox.getSource(BASE_WELL_RASTER_LAYER) as
        | mapboxgl.RasterSourceImpl
        | undefined;
      if (existingRasterSource) {
        if (
          typeof existingRasterSource.setTiles === "function" &&
          existingRasterSource.tiles?.[0] !== newRasterTileUrl
        ) {
          existingRasterSource.setTiles([newRasterTileUrl]);
        }
        if (mapbox.getLayer(BASE_WELL_RASTER_LAYER)) {
          mapbox.setLayoutProperty(
            BASE_WELL_RASTER_LAYER,
            "visibility",
            baseWellVisibility
          );
          mapbox.setLayerZoomRange(
            BASE_WELL_RASTER_LAYER,
            zoomSettings.minzoom,
            baseWellRasterMaxzoom
          );
        }
      } else if (mapbox.isStyleLoaded()) {
        addBaseWellRasterLayer();
      }
      applyBaseWellStyle(mapbox, baseWellStyle);
      return;
    }

    // Nothing on the map yet. useMapLifecycle's style.load pass owns creation
    // (getMapLayers declares both base-well layers), so while the style is
    // still parsing — every basemap switch passes through here — this effect
    // must stay out of the way and let that pass create them. It used to
    // pre-create them from a `once("idle")` callback, which fired inside the
    // lifecycle's 200 ms isStyleLoaded() poll gap and made its ordered add
    // find these layers already present.
    if (!mapbox.isStyleLoaded()) return;

    // Genuinely-missing case: the style has finished loading but the layers
    // aren't there, because the lifecycle pass ran before the org's
    // survey/IHS access resolved and getMapLayers skipped the base-well block.
    // Filtering by resource play is server-side now (baked into the filter
    // behind newTileUrl's `f` query param), so no client-side filter
    // expression is applied here.
    for (const id of [baseWellLinesLayerId, BASE_WELL_LAYER, BASE_WELL_RASTER_LAYER]) {
      if (mapbox.getLayer(id)) {
        mapbox.removeLayer(id);
      }
    }

    mapbox.addSource(baseWellSourceId, {
      type: "vector",
      promoteId: "uwi",
      tiles: [newTileUrl]
    });

    // Insert the base layers just below the interactive well layer when it's
    // present; otherwise add on top. Passing a beforeId that doesn't exist throws
    // and would abort the effect before the layers are added.
    const beforeId = mapbox.getLayer(WELL_LAYER) ? WELL_LAYER : undefined;

    // eva-map-svc #596: base_well_lines and base_wells are disjoint well sets
    // now (a well contributes to exactly one), not "wells" vs "the same
    // wells' survey lines" — see the matching comment in getMapLayers.ts.
    // base_well_lines carries EVERY horizontal well, at every zoom, including
    // one feature per multilateral leg (`group == "leg"`); BASE_WELL_PAINT
    // (baseWellStyle.ts) builds its dash with WELL_LAYER's wellLineDasharray, so
    // both draw the same pixel-length dash. Both layers set
    // their initial `visibility` from the persisted checkbox state so a
    // freshly-added layer never defaults to mapbox's implicit "visible"
    // while the checkbox is unchecked.
    mapbox.addLayer(
      {
        id: baseWellLinesLayerId,
        type: "line",
        source: `${BASE_WELL_LAYER}-lines`,
        "source-layer": "base_well_lines",
        minzoom: baseWellMvtMinzoom,
        maxzoom: zoomSettings.maxzoom,
        layout: {
          visibility: baseWellVisibility
        },
        paint: BASE_WELL_PAINT[BASE_WELL_LINES_LAYER](baseWellStyle)
      },
      beforeId
    );

    // base_wells now carries ONLY vertical (WellType.V) wells, at their
    // centroid — in a horizontal play this layer is nearly empty; it is not
    // "one dot per well" any more. The circle layer carries the
    // BASE_WELL_LAYER id for the layer-tree checkbox.
    mapbox.addLayer(
      {
        id: BASE_WELL_LAYER,
        type: "circle",
        source: `${BASE_WELL_LAYER}-lines`,
        "source-layer": "base_wells",
        minzoom: baseWellMvtMinzoom,
        maxzoom: zoomSettings.maxzoom,
        layout: {
          visibility: baseWellVisibility
        },
        paint: BASE_WELL_PAINT[BASE_WELL_LAYER](baseWellStyle)
      },
      beforeId
    );

    // Raster half of the crossover — see addBaseWellRasterLayer above.
    addBaseWellRasterLayer();
    applyBaseWellStyle(mapbox, baseWellStyle);
  }, [
    baseLayerFilterId,
    // Without this a preference change leaves the URL identical (the server
    // resolves the preference from the JWT) and setTiles never fires, so the
    // layer keeps rendering tiles built under the previous preference.
    surveyPreferenceToken,
    // Same reasoning, for the separately-settable header-source order — only
    // the MVT url embeds this token, but it still has to appear here so a
    // header-only reorder re-runs this effect and swaps that url in.
    headerPreferenceToken,
    orgId,
    // Re-apply visibility whenever the "Base Well" checkbox is toggled or
    // restored from session/view on load — see baseWellVisibility above.
    isBaseWellChecked,
    // Without these the effect can run once before the map/style exists and
    // never re-run, so the reconcile pass below never reaches the layers that
    // useMapLifecycle's style.load pass created. isMapInitialized flips on
    // every basemap switch (false at style.load, back to true once the
    // reload's txn resolves), which is exactly when those layers are
    // recreated.
    isMapInitialized,
    isStyleLoaded,
    displaySettings.layerZoomSettings?.[BASE_WELL_LAYER],
    baseLayerRefactor,
    baseWellStyle.color,
    baseWellStyle.opacity,
    baseWellStyle.thickness
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Base well "feed not ready yet" retry ───────────────────────────────
  // eva-map-svc#594 answers 503 + `Retry-After: 30` on the base-well tile
  // endpoints until its attribute store finishes its first load. The server
  // heals itself in ~30s, but the browser does not: mapbox-gl ignores
  // `Retry-After` and never re-requests a tile it has already failed until
  // that tile leaves the source cache. So a user who opened the map inside
  // that window sits in front of a checked checkbox and an empty layer until
  // they pan, zoom or reload.
  //
  // This waits out the server's retry window and then drops the source's
  // cached tiles so mapbox fetches them again.
  //
  // Deliberately a sibling of the base-well effect above rather than part of
  // it: that effect re-runs on plays / org / checkbox / style changes, and its
  // cleanup would then cancel a pending retry every time. This one subscribes
  // once for the component's life and reaches the live map through the ref, so
  // it cannot interfere with the basemap-switch reconciliation logic.
  const baseWellRetryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const baseWellRetryCountRef = useRef(0);
  useEffect(() => {
    if (!baseLayerRefactor) return;
    // Detaches the "a tile loaded" listener, which only exists while a retry
    // is actually pending.
    let detachSourceData: (() => void) | undefined;

    const clearPendingRetry = () => {
      if (baseWellRetryTimerRef.current) {
        clearTimeout(baseWellRetryTimerRef.current);
        baseWellRetryTimerRef.current = undefined;
      }
      detachSourceData?.();
      detachSourceData = undefined;
    };

    const scheduleRetry = () => {
      // Every failed tile in the viewport reports the same outage, and both of
      // the map's error listeners see each one — so this runs dozens of times
      // per not-ready window and must collapse to one timer.
      if (baseWellRetryTimerRef.current) return;
      // Hard cap. Never an unbounded timer: once it is spent, the user's next
      // pan or reload is the recovery path.
      if (baseWellRetryCountRef.current >= BASE_WELL_MAX_RETRIES) return;

      const mapbox = mapboxRef.current;
      if (!mapbox) return;

      const attempt = baseWellRetryCountRef.current + 1;
      baseWellRetryCountRef.current = attempt;

      // A tile that actually loads means the feed is up: drop the pending
      // retry so a refetch doesn't flash tiles that are already on screen.
      // mapbox only fires `sourcedata` with a `tile` on a successful load.
      // Either base-well source counts — a load on one strongly implies the
      // other pod-side feed is up too, and waiting on both independently
      // would just delay recovery for whichever loads second.
      const onSourceData = (e: { sourceId?: string; tile?: unknown }) => {
        if (
          (e?.sourceId !== BASE_WELL_SOURCE_ID &&
            e?.sourceId !== BASE_WELL_RASTER_SOURCE_ID) ||
          !e?.tile
        ) {
          return;
        }
        clearPendingRetry();
      };
      mapbox.on("sourcedata", onSourceData);
      detachSourceData = () => mapbox.off("sourcedata", onSourceData);

      // Linear backoff off the server's own Retry-After: 30s, 60s, 90s, so a
      // pod that is slow to load its attributes isn't hammered.
      baseWellRetryTimerRef.current = setTimeout(() => {
        baseWellRetryTimerRef.current = undefined;
        detachSourceData?.();
        detachSourceData = undefined;

        // Both base-well sources share the same not-ready window, so both
        // get reloaded together — otherwise whichever source didn't trigger
        // this retry would stay stuck showing its failed tiles.
        for (const sourceId of [BASE_WELL_SOURCE_ID, BASE_WELL_RASTER_SOURCE_ID]) {
          const source = mapboxRef.current?.getSource(sourceId) as
            | (mapboxgl.VectorSourceImpl & { reload?: () => void })
            | undefined;
          // VectorTileSource/RasterTileSource.reload() is public API in the
          // pinned mapbox-gl 2.15 and does exactly what's needed — it
          // re-reads the tile endpoint and clears the source's cached tiles,
          // so every tile that failed is requested again. No
          // removeSource/addSource, so the layers and the visibility and
          // zoom range the effect above reconciled stay put.
          source?.reload?.();
        }
        mapboxRef.current?.triggerRepaint?.();
      }, BASE_WELL_RETRY_BASE_MS * attempt);
    };

    const unsubscribe = onBaseWellTileNotReady(scheduleRetry);

    return () => {
      unsubscribe();
      clearPendingRetry();
    };
    // Subscribes once per mount — see the note above on why this must not
    // re-run with the base-well effect's dependencies.
  }, [baseLayerRefactor]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Legacy base well layer effect (EVA-35 flag off) ─────────────────────
  // Only a source URL change recreates it; visibility/zoom range apply in
  // place with no isStyleLoaded() gate — BASE_WELL_LAYER is exempt from
  // useManageMapboxLayerStyles, so this is its only zoom-range writer.
  // mapbox-gl populates a raster source's `tiles` one frame after addSource, so
  // the last URL this effect handed it stands in until then.
  const legacyBaseWellUrlRef = useRef<string>();
  useEffect(() => {
    if (baseLayerRefactor !== false) return;
    const mapbox = mapboxRef.current;
    if (!mapbox) return;

    // New-arch layers never belong on a flag-off map, whatever this org's
    // access — so this runs before the access gate. A BASE_WELL_LAYER layer
    // without a same-named source is the new-arch circle, not ours.
    const hasLegacySource = !!mapbox.getSource(BASE_WELL_LAYER);
    for (const id of [
      `${BASE_WELL_LAYER}-lines`,
      BASE_WELL_RASTER_LAYER,
      ...(hasLegacySource ? [] : [BASE_WELL_LAYER])
    ]) {
      if (mapbox.getLayer(id)) {
        mapbox.removeLayer(id);
      }
    }
    for (const id of [`${BASE_WELL_LAYER}-lines`, BASE_WELL_RASTER_LAYER]) {
      if (mapbox.getSource(id)) {
        mapbox.removeSource(id);
      }
    }

    if (!(hasSurvey || hasIhs || hasFeature("Force IHS Base Layer"))) return;

    const newTileUrl = getLegacyBaseWellLayerTileUrl({
      hasGdc,
      hasIhs,
      orgId,
      baseLayerResourcePlays,
      hasFeature
    });

    const zoomSettings =
      displaySettings.layerZoomSettings?.[BASE_WELL_LAYER] || BASE_WELL_DEFAULT_ZOOM;
    const visibility: "visible" | "none" = isBaseWellChecked ? "visible" : "none";

    const existingSource = mapbox.getSource(BASE_WELL_LAYER) as
      | (mapboxgl.RasterSourceImpl & {
          setTiles?: (tiles: string[]) => void;
          tiles?: string[];
        })
      | undefined;

    if (existingSource) {
      const currentUrl = existingSource.tiles?.[0] ?? legacyBaseWellUrlRef.current;
      if (typeof existingSource.setTiles === "function" && currentUrl !== newTileUrl) {
        existingSource.setTiles([newTileUrl]);
        legacyBaseWellUrlRef.current = newTileUrl;
      }
      if (mapbox.getLayer(BASE_WELL_LAYER)) {
        mapbox.setLayoutProperty(BASE_WELL_LAYER, "visibility", visibility);
        mapbox.setLayerZoomRange(
          BASE_WELL_LAYER,
          zoomSettings.minzoom,
          zoomSettings.maxzoom
        );
      }
      return;
    }

    // Create needs a loaded style.
    if (!mapbox.isStyleLoaded()) return;

    mapbox.addSource(BASE_WELL_LAYER, {
      type: "raster",
      tiles: [newTileUrl],
      tileSize: 256
    });

    mapbox.addLayer(
      {
        id: BASE_WELL_LAYER,
        type: "raster",
        source: BASE_WELL_LAYER,
        minzoom: zoomSettings.minzoom,
        maxzoom: zoomSettings.maxzoom,
        layout: { visibility },
        paint: {}
      },
      mapbox.getLayer(WELL_LAYER) ? WELL_LAYER : undefined
    );
    legacyBaseWellUrlRef.current = newTileUrl;
  }, [
    baseLayerRefactor,
    baseLayerResourcePlays,
    hasSurvey,
    hasIhs,
    hasGdc,
    hasFeature,
    orgId,
    isBaseWellChecked,
    // Same reason as the new-arch effect above: without these the effect can
    // run once before the map/style exists and never reach the layer the
    // style.load pass created — on first load and after every basemap switch.
    isMapInitialized,
    isStyleLoaded,
    displaySettings.layerZoomSettings?.[BASE_WELL_LAYER]
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Compose hooks (dependency order: leaves first) ─────────────────────

  // Phase 6 — Facility highlighting (no hook dependencies)
  const {
    highlightFacilities,
    clearHighlightedFacilities,
    getFacilityHighlightIconSizeExpression,
    getFacilityIconSizeExpression
  } = useMapFacilityHighlighting({ mapboxRef });

  // Phase 6 — Well highlighting (depends on facility highlighting expressions)
  const { getHighlightedLineThickness } = useMapWellHighlighting({
    mapboxRef,
    getFacilityHighlightIconSizeExpression,
    getFacilityIconSizeExpression,
    highlightedFeatures,
    selectedGroups,
    entitiesInGroup,
    isMapInitialized
  });

  // Phase 3 — Navigation (no hook dependencies)
  useMapNavigation({
    mapboxRef,
    entitiesInGroupRef,
    activeEntitiesRef,
    autoZoomSetting,
    zoomToFilterSetting,
    isMapInitialized
  });

  // Phase 2 — Attention overlay (depends on facility + well highlighting)
  const { clearHoverHighlighting, clearHoverHighlightingDebounced } =
    useMapAttentionOverlay({
      mapboxRef,
      addLayer,
      getHighlightedLineThickness,
      highlightFacilities,
      clearHighlightedFacilities,
      isMapInitialized,
      isSyncHighlightOff
    });

  // Phase 5 — Hover & legend (no hook dependencies beyond refs)
  const { onMouseMoveDebounce, onMouseLeaveDebounce } = useMapHoverLegend({
    mapboxRef,
    id,
    isMountedRef,
    mapDispatch,
    entitiesInGroupRef,
    facilitiesInGroupRef,
    isPolygonFilterControlVisibleRef,
    entitiesInGroup,
    facilitiesInGroup
  });

  // Phase 4 — Click selection (depends on hover debounces)
  useMapClickSelection({
    mapboxRef,
    isMountedRef,
    mapDispatch,
    capabilities,
    onMouseMoveDebounce,
    onMouseLeaveDebounce,
    entitiesInGroupRef,
    drawRef
  });

  // Label density (used by tile source manager + lifecycle)
  const { updateLabels } = useLabelDensity({
    mapboxRef,
    txnIdRef,
    isMapInitialized
  });

  // Phase 7 — Tile source manager (depends on capabilities, updateLabels)
  const {
    filterWellOnMap,
    updateWellsAndFacilitiesOnMap,
    canFilterWellsOnMap,
    tileUriRef
  } = useMapTileSourceManager({
    mapboxRef,
    isMountedRef,
    mapDispatch,
    txnIdRef,
    activeEntityKinds,
    isMapInitialized,
    updateLabels,
    capabilities
  });

  // Highlight selected wells/facilities from legend selection
  useHighlightSelection(mapboxRef, selectedGroups, entitiesInGroup);

  // Rig/Frac layer-local attribute filtering (EVA-63) AND session-only value
  // hiding (EVA-64) — the sole setFilter writer for these layers. Re-applies
  // on selection/hidden-value change and after style reload (isMapInitialized
  // mirrors the same signal useMapTileSourceManager keys its own filter
  // re-application on).
  useRigFracLayerFilter({
    mapboxRef,
    isMapInitialized,
    enabled: hasFeature("Rig And Frac Layers")
  });

  // Phase 8 — Map lifecycle (depends on all other hooks)
  useMapLifecycle({
    mapboxRef,
    drawRef,
    id,
    isMountedRef,
    mapDispatch,
    addLayer,
    viewLockedRef,
    viewLockedByDashboardRef,
    extentSyncFrozenRef,
    selectedGroupsRef,
    clearHoverHighlighting,
    clearHoverHighlightingDebounced,
    filterWellOnMap,
    updateWellsAndFacilitiesOnMap,
    canFilterWellsOnMap,
    updateLabels,
    tileUriRef,
    txnIdRef,
    checkedPadScenariosRef,
    entitiesInGroup,
    facilitiesInGroup,
    tokenRef,
    isDefaultMapSettingsLoaded,
    orgId,
    filterId: baseLayerFilterId,
    // getMapLayers recreates the base-well source on every style load (basemap
    // switch), so it needs the same preference token or it would rebuild the
    // source with a preference-less URL.
    prefHash: surveyPreferenceToken,
    attrPrefHash: headerPreferenceToken,
    baseWellStyle,
    wellLineThickness: displaySettings.wellLineThickness,
    onMouseMoveDebounce,
    onMouseLeaveDebounce,
    defaultBaseLayer,
    baseLayerRefactor,
    hasSurvey,
    hasGdc,
    hasIhs,
    baseLayerResourcePlays
  });

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div id={id} className="fill">
      <div
        id="map-screenshot-overlay"
        className="map-copy-overlay-container full-width full-height"
      />
    </div>
  );
}
